import React, { createContext, useContext, useRef, useCallback } from "react";

type TippyInstance = { hide: () => void; show: () => void; state: { isVisible: boolean } };

type TippyContextType = {
  register: (instance: TippyInstance) => void;
  unregister: (instance: TippyInstance) => void;
  closeAll: () => void;
  suppress: () => void;
  unsuppress: () => void;
};

const TippyContext = createContext<TippyContextType | null>(null);

export const TippyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const instances = useRef<TippyInstance[]>([]);
  const suppressed = useRef(false);
  const originalShowMap = useRef(new WeakMap<TippyInstance, TippyInstance["show"]>());

  const register = useCallback((instance: TippyInstance) => {
    instances.current.push(instance);
    if (suppressed.current) {
      instance.hide();
      // patch show() so it can't reopen while suppressed
      if (!originalShowMap.current.has(instance)) {
        originalShowMap.current.set(instance, instance.show);
        instance.show = () => {
          if (!suppressed.current) {
            originalShowMap.current.get(instance)?.call(instance);
          }
        };
      }
    }
  }, []);

  const unregister = useCallback((instance: TippyInstance) => {
    instances.current = instances.current.filter(i => i !== instance);
    originalShowMap.current.delete(instance);
  }, []);

  const closeAll = useCallback(() => {
    instances.current.forEach(i => i.hide());
  }, []);

  const suppress = useCallback(() => {
    suppressed.current = true;
    closeAll();
    // patch all active instances
    instances.current.forEach(i => {
      if (!originalShowMap.current.has(i)) {
        originalShowMap.current.set(i, i.show);
        i.show = () => {
          if (!suppressed.current) {
            originalShowMap.current.get(i)?.call(i);
          }
        };
      }
    });
  }, [closeAll]);

  const unsuppress = useCallback(() => {
    suppressed.current = false;
    // restore original show()
    instances.current.forEach(i => {
      const orig = originalShowMap.current.get(i);
      if (orig) {
        i.show = orig;
        originalShowMap.current.delete(i);
      }
    });
  }, []);

  return (
    <TippyContext.Provider value={{ register, unregister, closeAll, suppress, unsuppress }}>
      {children}
    </TippyContext.Provider>
  );
};

export const useTippyRegistry = () => {
  const ctx = useContext(TippyContext);
  if (!ctx) throw new Error("TippyProvider is missing");
  return ctx;
};
